import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      toast.success('Message sent successfully!');
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      if (errors) {
        Object.values(errors).forEach((msgs: any) => toast.error(msgs[0]));
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gray-900 text-white py-16 px-4 text-center">
        <p className="text-xs tracking-[0.3em] text-blue-400 uppercase mb-3">BabyCub Support</p>
        <h1 className="text-3xl md:text-4xl font-black">Contact Us</h1>
        <p className="mt-3 text-gray-400 text-sm max-w-md mx-auto">
          For order details, return, exchange or product information — BabyCub support is here to help.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-6 uppercase tracking-wider">Our Information</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Phone / WhatsApp</p>
                    <a href="tel:03363898930" className="text-sm text-gray-800 hover:text-blue-600 transition-colors font-medium">03363898930</a>
                    <p className="text-xs text-gray-400 mt-0.5">Call or WhatsApp</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Email</p>
                    <a href="mailto:babycub@gmail.com" className="text-sm text-gray-800 hover:text-blue-600 transition-colors font-medium">babycub@gmail.com</a>
                    <p className="text-xs text-gray-400 mt-0.5">We reply within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Address</p>
                    <p className="text-sm text-gray-800 font-medium">Street No. 3, Babar Colony</p>
                    <p className="text-sm text-gray-600">Abu Dhabi Road, Rahim Yar Khan</p>
                    <p className="text-sm text-gray-600">Punjab, Pakistan</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Business Hours</h3>
              <div className="space-y-2 text-sm">
                {[
                  { day: 'Monday – Friday', hours: '10:00 AM – 7:00 PM' },
                  { day: 'Saturday', hours: '10:00 AM – 5:00 PM' },
                  { day: 'Sunday', hours: 'Closed' },
                ].map(row => (
                  <div key={row.day} className="flex justify-between">
                    <span className="text-gray-600">{row.day}</span>
                    <span className={`font-medium ${row.hours === 'Closed' ? 'text-red-500' : 'text-gray-900'}`}>{row.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-10 bg-green-50 rounded-2xl border border-green-100">
                <CheckCircle size={56} className="text-green-500 mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h2>
                <p className="text-gray-600 text-sm max-w-sm">
                  Thank you for reaching out. We've received your message and will get back to you within 24 hours.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                  className="mt-6 bg-gray-900 text-white px-6 py-2.5 text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wider mb-6">Send a Message</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Full Name *</label>
                    <input
                      type="text" name="name" value={form.name} onChange={handleChange} required
                      placeholder="Your full name"
                      className="w-full border border-gray-200 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-gray-900 bg-gray-50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Email Address *</label>
                    <input
                      type="email" name="email" value={form.email} onChange={handleChange} required
                      placeholder="your@email.com"
                      className="w-full border border-gray-200 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-gray-900 bg-gray-50 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Subject *</label>
                  <select
                    name="subject" value={form.subject} onChange={handleChange} required
                    aria-label="Subject"
                    title="Subject"
                    className="w-full border border-gray-200 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-gray-900 bg-gray-50 transition-colors"
                  >
                    <option value="">Select a subject...</option>
                    <option value="Order Inquiry">Order Inquiry</option>
                    <option value="Product Question">Product Question</option>
                    <option value="Return & Refund">Return & Refund</option>
                    <option value="Shipping Issue">Shipping Issue</option>
                    <option value="Payment Problem">Payment Problem</option>
                    <option value="Size & Fit Help">Size & Fit Help</option>
                    <option value="Wholesale Inquiry">Wholesale Inquiry</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Message *</label>
                  <textarea
                    name="message" value={form.message} onChange={handleChange} required rows={6}
                    placeholder="Please describe your question or concern in detail..."
                    className="w-full border border-gray-200 px-4 py-3 text-sm rounded-lg focus:outline-none focus:border-gray-900 bg-gray-50 transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit" disabled={loading}
                  className="w-full bg-gray-900 text-white py-3.5 text-sm font-bold uppercase tracking-widest rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                  ) : (
                    <><Send size={15} /> Send Message</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
